"use client";

import { IAirdropTaskRender } from "@/types/task-renderer.types";
import { TaskTypes } from "@/config/task-types";
import { DefaultTaskRenderer } from "./DefaultTaskRenderer";
import { ClickLinkRenderer } from "./ClickLinkRenderer";
import { MediaRenderer } from "./MediaRenderer";
import { SecretCodeRenderer } from "./SecretCodeRenderer";
import { QuizRenderer } from "./QuizRenderer";
import { DiscordRoleRenderer } from "./DiscordRoleRenderer";
import { ProductHuntStepRenderer } from "./ProductHuntStepRenderer";
import { FEATURED_TASK_GUID } from "@/config/featured-task";
import { useGetSocialUser, useSocialPayMutations } from "@/hooks/social-pay";
import { useConnection } from "@/contexts/connection-context";
import { AuthenticationNotice } from "./components/authentication-notice";
import { TwitterLinkNotice } from "./components/twitter-link-notice";
import { DiscordLinkNotice } from "./components/discord-link-notice";
import { TwitterSessionExpiredNotice } from "./components/twitter-session-expired-notice";
import { DiscordSessionExpiredNotice } from "./components/discord-session-expired-notice";
import { TaskLoading } from "./components/task-loading";

// Define which tasks require which social auth
const TWITTER_TASKS: string[] = [TaskTypes.FOLLOW_TWITTER, TaskTypes.RETWEET, TaskTypes.RETWEET_MVX, TaskTypes.TWITTER_USERNAME, TaskTypes.TWEET_KEYWORDS, TaskTypes.LIKE_POST, TaskTypes.TWITTER_POST];

const DISCORD_TASKS: string[] = [TaskTypes.JOIN_DISCORD, TaskTypes.DISCORD_ROLE];

export const QuestRenderer = ({ task, campaign, address, challengesUser, completeTaskMutation }: IAirdropTaskRender) => {
  const { isAuthenticated } = useConnection();
  const { data: socialUser, isLoading: isSocialUserLoading } = useGetSocialUser();
  const { linkTwitter, linkDiscord } = useSocialPayMutations();

  // Show loading during initial data fetch
  const isInitialLoading = isSocialUserLoading || (isAuthenticated && TWITTER_TASKS.includes(task.taskName) && isSocialUserLoading);

  if (isInitialLoading) {
    return <TaskLoading message="Loading your data..." />;
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <AuthenticationNotice />;
  }

  // Check social auth requirements
  const needsTwitterAuth = TWITTER_TASKS.includes(task.taskName) && task.taskName !== TaskTypes.TWITTER_USERNAME && !socialUser?.twitterMetadata?.username;

  const needsDiscordAuth = DISCORD_TASKS.includes(task.taskName) && !socialUser?.discordMetadata?.username;

  // Check for expired sessions
  const needsTwitterReconnect =
    TWITTER_TASKS.includes(task.taskName) &&
    task.taskName !== TaskTypes.TWITTER_USERNAME &&
    !(socialUser?.twitterMetadata?.encryptedAccessToken || socialUser?.twitterMetadata?.encryptedRefreshToken);

  const needsDiscordReconnect =
    DISCORD_TASKS.includes(task.taskName) &&
    socialUser?.discordMetadata?.username &&
    !(socialUser?.discordMetadata?.encryptedAccessToken || socialUser?.discordMetadata?.encryptedRefreshToken);

  // Render appropriate UI based on auth state
  if (needsTwitterAuth) {
    return <TwitterLinkNotice onConnect={(redirectUrl) => linkTwitter.mutate({ redirectUrl })} />;
  }

  if (needsDiscordAuth) {
    return <DiscordLinkNotice onConnect={() => linkDiscord.mutate()} />;
  }

  if (needsTwitterReconnect && socialUser?.twitterMetadata) {
    return <TwitterSessionExpiredNotice onReconnect={() => linkTwitter.mutate({ redirectUrl: window.location.href })} twitterMetadata={socialUser.twitterMetadata} />;
  }

  if (needsDiscordReconnect && socialUser?.discordMetadata) {
    return <DiscordSessionExpiredNotice onReconnect={() => linkDiscord.mutate()} discordMetadata={socialUser.discordMetadata} />;
  }

  // User is authenticated and has required social auth
  const rendererProps = {
    task,
    completeTaskMutation,
    campaign,
    address,
    challengesUser,
  };

  // Featured task gets its own multi-step UI
  if (task.taskGuid === FEATURED_TASK_GUID) {
    return <ProductHuntStepRenderer {...rendererProps} />;
  }

  // Route based on task name
  switch (task.taskName) {
    // Link Tasks
    case TaskTypes.CLICK_LINK:
    case TaskTypes.SHARE_PROGRESS:
      return <ClickLinkRenderer {...rendererProps} />;

    // Media Tasks
    case TaskTypes.CREATE_MEDIA:
    case TaskTypes.TWEET_KEYWORDS:
    case TaskTypes.TWITTER_POST:
      // Use email placeholder for EthosX tasks
      if (task.type === "EthosX Challenges") {
        return <MediaRenderer {...rendererProps} placeholder="johndoe@email.com" />;
      }
      return <MediaRenderer {...rendererProps} />;

    case TaskTypes.RHUNA_QUERY:
      return <MediaRenderer {...rendererProps} placeholder="+40234567890" />;

    // Secret Code Task
    case TaskTypes.SECRET_CODE:
      return <SecretCodeRenderer {...rendererProps} />;

    // Quiz Task
    case TaskTypes.QUIZ:
      return <QuizRenderer {...rendererProps} />;

    // Discord Role Task
    case TaskTypes.DISCORD_ROLE:
      return <DiscordRoleRenderer {...rendererProps} />;

    // All other tasks use default renderer (includes social tasks, etc.)
    default:
      return <DefaultTaskRenderer {...rendererProps} />;
  }
};
